import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Swal from 'sweetalert2';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Form,
} from 'react-bootstrap';
import moment from 'moment';

import { getAllQnAs, updateQnAById } from 'apis/admin/qnaApi';

import SimpleHeader from 'components/Headers/SimpleHeader.js';

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }
      </label>
    </div>
  ),
  paginationTotalRenderer: (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      { from } - { to } / Total { size }
    </span>
  ),
});

const { SearchBar } = Search;

function Index() {
  const user = useSelector(state => state.user);
  const componentRef = useRef();
  const [postList, setPostList] = useState([]);
  const [checked, setChecked] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('완료');

  useEffect(() => {
    getPosts();
  }, []);

  const getPosts = async () => {
    setPostList([]);
    await getAllQnAs()
      .then(response => {
        if (response.data.success) {
          setPostList(response.data.result);
        }
      })
      .catch(error => {
        console.error('error occured in board/QnA Index.js - getPosts() ', error);

        Swal.fire({
          icon: 'error',
          title: '문의 내역을 불러올 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  const updateStatusById = () => {
    Swal.fire({
      icon: 'warning',
      title: '문의 상태를 변경하시겠습니까?',
      showCancelButton: true,
      confirmButtonText: '문의 상태 변경',
      cancelButtonText: '취소',
    }).then(async (value) => {
      if (value.isConfirmed) {
        await updateQnAById({ idx: checked, status: selectedStatus })
          .then((response) => {
            if (response.data.success) {
              Swal.fire({
                icon: 'success',
                title: '상태 변경을 완료했습니다.',
              }).then(() => {
                window.location.reload();
              });
            }
          })
          .catch(() => {
            Swal.fire({
              icon: 'error',
              title: '상태 변경을 할 수 없습니다.',
              text: '잠시 후 다시 시도해주세요.',
            });
          });
      } else {
        Swal.close();
      }
    });
  };

  // 테이블 컬럼
  const columns = [
    {
      dataField: 'id',
      text: '번호',
      sort: true,
      searchable: false,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'name',
      text: '성함',
      sort: true,
      searchable: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'phone',
      text: '연락처',
      sort: false,
      searchable: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) => cell ? cell.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`) : '-',
    },
    {
      dataField: 'content',
      text: '문의 내용',
      sort: false,
      searchable: true,
      headerAlign: 'center',
      align: 'left',
      style: { maxWidth: '318px', whiteSpace: 'pre-line' },
      formatter: (cell) => <span dangerouslySetInnerHTML={{ __html: cell }} />
    },
    {
      dataField: 'createdAt',
      text: '문의 날짜',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) =>
        (cell && moment(cell).format('YYYY[-]MM[-]DD')) || '-',
    }, 
    {
      dataField: 'status',
      text: '상태',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) => cell === '대기' ? <span className="text-info">대기</span> : <span className="text-success">완료</span>
    },
  ];

  // 데이터 선택
  const selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    onSelect: (row) => {
      const currentChecked = checked.indexOf(row.id);
      const newChecked = [...checked];

      if (currentChecked === -1) {
        newChecked.push(row.id);
      } else {
        newChecked.splice(currentChecked, 1);
      }

      setChecked(newChecked);
    },
    onSelectAll: (isSelect, rows) => {
      if (isSelect) {
        const newChecked = [];
        rows.map((val) => newChecked.push(val.id));
  
        setChecked(newChecked);
      } else {
        setChecked([]);
      }
    },
  };

  return (
    <>
      <SimpleHeader parentName="예약 및 문의" name="문의 관리" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <ToolkitProvider
                data={postList}
                keyField="id"
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <Container fluid>
                      <Row>
                        <Col xs={12} sm={6}>
                          <div
                            id="datatable-basic_filter"
                            className="dataTables_filter"
                          >
                            <label>
                              <span className="text-sm mr-2">
                                <i className="fas fa-search text-muted" />
                              </span>
                              <SearchBar
                                className="form-control-sm"
                                {...props.searchProps}
                                placeholder="성함, 문의 내용 등..."
                              />
                            </label>
                          </div>
                        </Col>
                        <Col xs={12} sm={6}>
                        {user &&
                            user.userData &&
                            user.userData.isAdmin &&
                            checked.length > 0 && (
                              <Row>
                                <Col />
                                <Col
                                  sm={6}
                                  md={8}
                                  className="d-flex flex-row justify-content-lg-end"
                                >
                                  <Form.Control
                                    as="select"
                                    custom
                                    size="sm"
                                    className="w-50 mr-2"
                                    defaultValue={selectedStatus}
                                    onChange={(e) =>
                                      setSelectedStatus(e.target.value)
                                    }
                                  >
                                    <option value="완료">완료</option>
                                    <option value="대기">대기</option>
                                  </Form.Control>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={updateStatusById}
                                  >
                                    상태 변경
                                  </Button>
                                </Col>
                              </Row>
                            )}
                        </Col>
                      </Row>
                    </Container>
                    <BootstrapTable
                      ref={componentRef}
                      {...props.baseProps}
                      bootstrap4={true}
                      pagination={pagination}
                      bordered={false}
                      selectRow={selectRow}
                      noDataIndication={() => <div className="text-center text-muted">데이터가 없습니다.</div>}
                    />
                  </div>
                )}
              </ToolkitProvider>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default Index;
